// Claude: redesigned — asset type dropdown, URL fetch button, auto-title, no summary 30-03-2026
import { Box, Button, Flex, Input, Text, VStack, Select } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { MdClose } from "react-icons/md";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import { SellerAPI } from "../../Apis/sellerAPI";
import { errorHandler } from "../../Utils/handler";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../redux/Actions";
import { ASSET_TYPES } from "../../Constants/data";

type Props = {
  handleCloseModal: () => void;
  sellerId: string;
  addUrl: boolean;
  // Claude: edit mode — pass existing asset to pre-fill fields 25-03-2026
  editAsset?: { _id: string; assetTitle: string; assetUrl: string; assetType?: string; coverImage?: string } | null;
};

const AddUrlModal = (props: Props) => {
  const sellerAsset = useSelector((state: any) => state.sellerAsset);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  // Claude: asset type (business category) 30-03-2026
  const [assetType, setAssetType] = useState<string>("");
  // Claude: optional cover image URL for buyer feed 05-05-2026
  const [coverImage, setCoverImage] = useState<string>("");
  // Claude: fetch state — fetchDone gates Save; fetchResult holds file type + final URL 30-03-2026
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchDone, setFetchDone] = useState(false);
  const [fetchError, setFetchError] = useState<string>("");
  const [fileType, setFileType] = useState<string>("");
  const [finalUrl, setFinalUrl] = useState<string>("");
  // Claude: submit error shown inline so seller can read it 30-03-2026
  const [submitError, setSubmitError] = useState<string>("");
  const { success, error } = useToastWrapper();

  const isEditMode = !!props.editAsset;

  useEffect(() => {
    if (isEditMode && props.editAsset) {
      // Claude: pre-fill fields for edit mode 30-03-2026
      dispatch(actions.sellerAssets.setTitle({ title: props.editAsset.assetTitle }));
      dispatch(actions.sellerAssets.setLink({ link: props.editAsset.assetUrl }));
      setAssetType(props.editAsset.assetType || "");
      setCoverImage(props.editAsset.coverImage || "");
      // In edit mode allow Save without re-fetching
      setFetchDone(true);
    } else {
      dispatch(actions.sellerAssets.resetForm({}));
      setAssetType("");
      setCoverImage("");
      setFetchDone(false);
      setFetchError("");
      setFileType("");
      setFinalUrl("");
    }
  }, []);

  // Claude: Fetch button handler — validates URL, detects file type + title 30-03-2026
  const handleFetch = async () => {
    const url = sellerAsset.link?.trim();
    if (!url) { error("Please enter a URL first."); return; }
    setFetchLoading(true);
    setFetchDone(false);
    setFetchError("");
    setFileType("");
    setFinalUrl("");
    try {
      const result = await SellerAPI.fetchAssetInfo(url);
      if (result?.error) {
        setFetchError(result.error);
        setFetchDone(false);
      } else {
        setFileType(result?.fileType || "Unknown");
        setFinalUrl(result?.finalUrl || "");
        if (result?.title) {
          dispatch(actions.sellerAssets.setTitle({ title: result.title }));
        }
        setFetchDone(true);
      }
    } catch (err) {
      setFetchError("Could not reach this URL. Please check and try again.");
      setFetchDone(false);
    } finally {
      setFetchLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setSubmitError("");
    try {
      if (isEditMode && props.editAsset) {
        // Claude: edit mode — PATCH existing asset 30-03-2026
        await SellerAPI.updateAsset(props.editAsset._id, {
          assetTitle: sellerAsset.title,
          assetUrl: sellerAsset.link,
          assetType: assetType || undefined,
          coverImage: coverImage.trim() || undefined,
        });
        success("Asset updated", "");
      } else {
        // Claude: add mode — POST new asset 30-03-2026
        await SellerAPI.addAsset({
          sellerId: props.sellerId,
          assetTitle: sellerAsset.title,
          assetUrl: sellerAsset.link,
          assetType: assetType || undefined,
          coverImage: coverImage.trim() || undefined,
        });
        success("Asset added", "");
      }

      dispatch(actions.sellerAssets.resetForm({}));
      const res = await SellerAPI.getSellerAssets(props.sellerId);
      if (res) dispatch(actions.sellerAssets.setAssets({ links: res.data.data }));
      props.handleCloseModal();
    } catch (err) {
      const msg = errorHandler(err);
      setSubmitError(msg);
      error(msg, "");
    } finally {
      setLoading(false);
    }
  };

  const isSaveDisabled = !fetchDone || !assetType || !sellerAsset.title?.trim();

  return (
    <Box w={"100%"} h={"100%"} pt={"8px"} pb={"16px"} overflowY={"auto"} maxH={"80vh"}>
      <VStack position={"relative"} display={"flex"} justifyContent={"center"} alignItems={"center"} className={"create-meeting-modal"}>
        <Box position={"absolute"} right={0} top={0} pb={3} cursor={"pointer"} zIndex={200}>
          <MdClose size={20} onClick={props.handleCloseModal} />
        </Box>
      </VStack>

      <Box position={"relative"} display={"flex"} justifyContent={"center"} alignItems={"center"} flexDirection={"column"}>
        <Text fontSize={"md"} fontWeight={"regular"} textAlign={"center"} color={"black"} mb={"15px"}>
          {isEditMode ? "Edit asset" : "Add asset"}
        </Text>
      </Box>

      <Box>
        {/* Asset Type */}
        <Text fontSize={14} fontWeight={"normal"} mb={1}>Asset Type</Text>
        <Select
          placeholder="Select asset type"
          mb={4}
          h={"40px"}
          fontSize={14}
          borderColor={"gray.300"}
          value={assetType}
          onChange={(e) => setAssetType(e.target.value)}
        >
          {ASSET_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </Select>

        {/* URL + Fetch button */}
        <Text fontSize={14} fontWeight={"normal"} mb={1}>URL</Text>
        <Flex gap={2} mb={fetchError ? 1 : fetchDone ? 1 : 4} alignItems={"center"}>
          <Input
            type="text"
            flex={1}
            border={"1px"} outline={"none"} _focus={{ outline: "none", border: "0px" }}
            h={"40px"} px={2} borderColor={"gray.300"}
            value={sellerAsset.link}
            onChange={(e) => {
              dispatch(actions.sellerAssets.setLink({ link: e.target.value }));
              // Reset fetch state when URL changes
              setFetchDone(false);
              setFetchError("");
              setFileType("");
              setFinalUrl("");
            }}
          />
          <Button
            onClick={handleFetch}
            isLoading={fetchLoading}
            h={"40px"} px={4}
            bg={"#0E122F"} color={"white"}
            _hover={{ bg: "#2E7D32" }}
            borderRadius={"4px"}
            fontSize={"sm"}
            flexShrink={0}
          >
            Fetch
          </Button>
        </Flex>

        {/* Fetch error */}
        {fetchError && (
          <Text fontSize={11} color={"red.500"} mb={3}>{fetchError}</Text>
        )}

        {/* Fetch result fine print */}
        {fetchDone && !fetchError && (
          <Box mb={3}>
            {fileType && (
              <Text fontSize={11} color={"gray.500"}>File type: {fileType}</Text>
            )}
            {finalUrl && (
              <Text fontSize={11} color={"gray.500"} wordBreak="break-all">Resolved to: {finalUrl}</Text>
            )}
          </Box>
        )}

        {/* Claude: Title — hidden until Fetch succeeds; hint shown above 30-03-2026 */}
        {fetchDone && (
          <>
            <Text fontSize={11} color={"gray.500"} mb={1}>
              This title is used to match your asset with relevant buyers. Make it descriptive.
            </Text>
            {!sellerAsset.title?.trim() && (
              <Text fontSize={11} color={"orange.500"} mb={1}>
                Could not auto-detect title. Please enter it manually.
              </Text>
            )}
            <Text fontSize={14} fontWeight={"normal"} mb={1}>Title</Text>
            <Input
              type="text"
              mb={4}
              border={"1px"} outline={"none"} _focus={{ outline: "none", border: "0px" }}
              h={"40px"} w={"100%"} px={2} borderColor={"gray.300"}
              value={sellerAsset.title}
              onChange={(e) => dispatch(actions.sellerAssets.setTitle({ title: e.target.value }))}
            />
            {/* Claude: optional cover image URL — shown in buyer feed instead of OG proxy 05-05-2026 */}
            <Text fontSize={14} fontWeight={"normal"} mb={1}>Cover Image URL <Text as="span" fontSize={11} color="gray.400">(optional)</Text></Text>
            <Text fontSize={11} color={"gray.500"} mb={1}>If provided, this image is shown in the buyer feed instead of the auto-detected preview.</Text>
            <Input
              type="text"
              mb={4}
              border={"1px"} outline={"none"} _focus={{ outline: "none", border: "0px" }}
              h={"40px"} w={"100%"} px={2} borderColor={"gray.300"}
              placeholder="https://example.com/image.jpg"
              value={coverImage}
              onChange={(e) => setCoverImage(e.target.value)}
            />
          </>
        )}
      </Box>

      {submitError && (
        <Text fontSize={11} color={"red.500"} mb={2}>{submitError}</Text>
      )}
      <Flex w={"100%"} justifyContent={"space-between"} mt={2} gap={2}>
        <Button
          onClick={props.handleCloseModal}
          flex={1} size={"md"} variant={"outline"}
          borderColor={"card.borderLight"} _hover={{ background: "secondary.light" }} borderRadius={"2px"}
        >
          <Text fontSize={"sm"}>Cancel</Text>
        </Button>
        <Button
          onClick={handleSubmit}
          isLoading={loading}
          flex={1} size={"md"}
          bg={"#0E122F"} color={"white"} _hover={{ bg: "#2E7D32" }} borderRadius={"2px"}
          isDisabled={isSaveDisabled}
        >
          <Text fontSize={"sm"}>{isEditMode ? "Update" : "Add"}</Text>
        </Button>
      </Flex>
    </Box>
  );
};

export default AddUrlModal;
